/**
 * custom-order-form.js — WO-24-v3-SHOPIFY-NATIVE
 *
 * Responsibilities:
 * 1. Set minimum event date (today + 7 days)
 * 2. Progressive disclosure (no-op in v3 — all fields visible per Amendment §4)
 * 3. Pack all form fields into contact[body] as structured JSON before submit
 * 4. Client-side validation (required fields, email, phone, date >= today+7)
 * 5. Error summary / scroll-to-first-error
 * 6. Submit spinner / disabled state
 *
 * CONSTRAINTS:
 * - Vanilla JS only — no fetch() to external endpoints
 * - Form submits natively via browser POST to /contact
 * - No Turnstile, no file upload, no third-party references
 */

(function () {
  'use strict';

  /* ── Constants ────────────────────────────────────────────────── */
  var FORM_ID       = 'custom-order-form';
  var BODY_FIELD_ID = 'contact-body-json';
  var ERROR_SUMMARY = 'form-error-summary';
  var ERROR_LIST    = 'form-error-list';
  var SERVICE_ERROR = 'service-type-error';
  var SUBMIT_BTN    = 'custom-order-submit';
  var MIN_DAYS_OUT  = 7;

  /* ── Helpers ──────────────────────────────────────────────────── */
  function $(id) { return document.getElementById(id); }

  function addDays(date, days) {
    var d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  function formatDate(d) {
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  function isValidEmail(val) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val.trim());
  }

  function isValidPhone(val) {
    // Accept 7-15 digit patterns with optional +, spaces, dashes, parens
    return /^\+?[\d\s\-().]{7,20}$/.test(val.trim());
  }

  function getFieldValue(name) {
    var el = document.querySelector('[name="contact[' + name + ']"]');
    return el ? el.value.trim() : '';
  }

  function getCheckedBoxes(prefix) {
    var inputs = document.querySelectorAll('[name^="contact[' + prefix + '"]]:checked');
    var vals = [];
    for (var i = 0; i < inputs.length; i++) {
      vals.push(inputs[i].value);
    }
    return vals;
  }

  function showError(inputEl, message) {
    if (!inputEl) return;
    inputEl.classList.add('has-error');
    inputEl.setAttribute('aria-invalid', 'true');

    // Remove existing error span
    var existingErr = inputEl.parentNode.querySelector('.custom-order-form__field-error');
    if (existingErr) existingErr.remove();

    var errSpan = document.createElement('span');
    errSpan.className = 'custom-order-form__field-error';
    errSpan.setAttribute('role', 'alert');
    errSpan.textContent = message;
    inputEl.parentNode.appendChild(errSpan);
  }

  function clearFieldError(inputEl) {
    if (!inputEl) return;
    inputEl.classList.remove('has-error');
    inputEl.removeAttribute('aria-invalid');
    var errSpan = inputEl.parentNode.querySelector('.custom-order-form__field-error');
    if (errSpan) errSpan.remove();
  }

  function clearAllErrors(form) {
    var errored = form.querySelectorAll('.has-error');
    for (var i = 0; i < errored.length; i++) {
      errored[i].classList.remove('has-error');
      errored[i].removeAttribute('aria-invalid');
    }
    var errSpans = form.querySelectorAll('.custom-order-form__field-error');
    for (var j = 0; j < errSpans.length; j++) {
      errSpans[j].remove();
    }
    var summary = $(ERROR_SUMMARY);
    if (summary) summary.hidden = true;
    var svcErr = $(SERVICE_ERROR);
    if (svcErr) svcErr.hidden = true;
  }

  function showErrorSummary(errors) {
    var summary = $(ERROR_SUMMARY);
    var list    = $(ERROR_LIST);
    if (!summary || !list) return;

    list.innerHTML = '';
    for (var i = 0; i < errors.length; i++) {
      var li = document.createElement('li');
      li.textContent = errors[i].message;
      list.appendChild(li);
    }
    summary.hidden = false;
  }

  /* ── Set minimum date ─────────────────────────────────────────── */
  function initMinDate() {
    var dateInput = document.getElementById('order-event-date');
    if (!dateInput) return;
    var minDate = addDays(new Date(), MIN_DAYS_OUT);
    dateInput.min = formatDate(minDate);
  }

  /* ── Build structured body JSON ───────────────────────────────── */
  function buildBodyJSON(form) {
    var serviceTypes = [];
    var serviceCheckboxes = form.querySelectorAll('[data-service-type]:checked');
    for (var i = 0; i < serviceCheckboxes.length; i++) {
      serviceTypes.push(serviceCheckboxes[i].value);
    }

    var body = {
      form_version:        'WO-24-v3',
      request_source:      'shopify_contact_form_v3',
      submitted_at:        new Date().toISOString(),
      first_name:          getFieldValue('first_name'),
      last_name:           getFieldValue('last_name'),
      email:               getFieldValue('email'),
      phone:               getFieldValue('phone'),
      event_date:          getFieldValue('event_date'),
      event_start_time:    getFieldValue('event_start_time'),
      duration:            getFieldValue('duration'),
      guest_count:         getFieldValue('guest_count'),
      venue_name:          getFieldValue('venue_name'),
      venue_address:       getFieldValue('venue_address'),
      service_types:       serviceTypes,
      flavor_notes:        getFieldValue('flavor_notes'),
      addon_dips:          !!form.querySelector('#addon-dips:checked'),
      addon_toppings:      !!form.querySelector('#addon-toppings:checked'),
      notes:               getFieldValue('notes'),
      marketing_consent:   !!form.querySelector('#order-marketing-consent:checked')
    };

    return JSON.stringify(body);
  }

  /* ── Validate ─────────────────────────────────────────────────── */
  function validate(form) {
    var errors = [];

    // First name
    var firstNameEl = document.getElementById('order-first-name');
    if (!firstNameEl || !firstNameEl.value.trim()) {
      errors.push({ el: firstNameEl, message: 'First name is required.' });
    }

    // Last name
    var lastNameEl = document.getElementById('order-last-name');
    if (!lastNameEl || !lastNameEl.value.trim()) {
      errors.push({ el: lastNameEl, message: 'Last name is required.' });
    }

    // Email
    var emailEl = document.getElementById('order-email');
    if (!emailEl || !emailEl.value.trim()) {
      errors.push({ el: emailEl, message: 'Email address is required.' });
    } else if (!isValidEmail(emailEl.value)) {
      errors.push({ el: emailEl, message: 'Please enter a valid email address.' });
    }

    // Phone
    var phoneEl = document.getElementById('order-phone');
    if (!phoneEl || !phoneEl.value.trim()) {
      errors.push({ el: phoneEl, message: 'Phone number is required.' });
    } else if (!isValidPhone(phoneEl.value)) {
      errors.push({ el: phoneEl, message: 'Please enter a valid phone number.' });
    }

    // Event date — required + min 7 days out
    var dateEl = document.getElementById('order-event-date');
    if (!dateEl || !dateEl.value) {
      errors.push({ el: dateEl, message: 'Event date is required.' });
    } else {
      // Parse as local date (YYYY-MM-DD) to avoid timezone shift
      var parts = dateEl.value.split('-');
      var eventDate = new Date(
        parseInt(parts[0], 10),
        parseInt(parts[1], 10) - 1,
        parseInt(parts[2], 10)
      );
      var minDate = addDays(new Date(), MIN_DAYS_OUT);
      minDate.setHours(0, 0, 0, 0);
      if (eventDate < minDate) {
        errors.push({
          el: dateEl,
          message: 'Event date must be at least ' + MIN_DAYS_OUT + ' days from today. Please contact us directly for rush inquiries.'
        });
      }
    }

    // Guest count
    var guestEl = document.getElementById('order-guest-count');
    if (!guestEl || !guestEl.value.trim()) {
      errors.push({ el: guestEl, message: 'Estimated guest count is required.' });
    } else if (parseInt(guestEl.value, 10) < 1) {
      errors.push({ el: guestEl, message: 'Please enter a valid guest count.' });
    }

    // Venue name
    var venueEl = document.getElementById('order-venue-name');
    if (!venueEl || !venueEl.value.trim()) {
      errors.push({ el: venueEl, message: 'Venue name is required.' });
    }

    // Service type — at least one checked
    var serviceChecked = form.querySelectorAll('[data-service-type]:checked');
    if (serviceChecked.length === 0) {
      errors.push({ el: null, message: 'Please select at least one service type.', isServiceType: true });
    }

    return errors;
  }

  /* ── Handle submit ────────────────────────────────────────────── */
  function onSubmit(e) {
    var form = e.target;

    // Clear previous errors
    clearAllErrors(form);

    // Validate
    var errors = validate(form);

    if (errors.length > 0) {
      e.preventDefault();

      // Mark field errors
      var firstErrorEl = null;
      for (var i = 0; i < errors.length; i++) {
        if (errors[i].isServiceType) {
          var svcErr = $(SERVICE_ERROR);
          if (svcErr) svcErr.hidden = false;
          if (!firstErrorEl) {
            firstErrorEl = $(SERVICE_ERROR) || document.getElementById('service-type-grid');
          }
        } else if (errors[i].el) {
          showError(errors[i].el, errors[i].message);
          if (!firstErrorEl) firstErrorEl = errors[i].el;
        }
      }

      // Show summary
      showErrorSummary(errors);

      // Scroll to first error or summary
      var scrollTarget = $(ERROR_SUMMARY) || firstErrorEl;
      if (scrollTarget) {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (firstErrorEl && firstErrorEl.focus) firstErrorEl.focus();
      }

      return;
    }

    // Pack structured JSON into hidden body field
    var bodyField = $(BODY_FIELD_ID);
    if (bodyField) {
      bodyField.value = buildBodyJSON(form);
    }

    // Show spinner, disable submit
    var submitBtn = $(SUBMIT_BTN);
    if (submitBtn) {
      submitBtn.disabled = true;
      var spinner = submitBtn.querySelector('.custom-order-form__submit-spinner');
      var submitText = submitBtn.querySelector('.custom-order-form__submit-text');
      if (spinner) spinner.hidden = false;
      if (submitText) submitText.textContent = 'Sending\u2026';
    }

    // Allow native form POST to /contact to proceed
  }

  /* ── Live validation on blur ──────────────────────────────────── */
  function attachLiveValidation(form) {
    var requiredInputs = form.querySelectorAll('[required]');
    for (var i = 0; i < requiredInputs.length; i++) {
      (function (el) {
        el.addEventListener('blur', function () {
          if (!el.value.trim()) {
            // Only show error on blur if user interacted
          } else {
            clearFieldError(el);
          }
        });
        el.addEventListener('input', function () {
          if (el.classList.contains('has-error')) {
            if (el.value.trim()) clearFieldError(el);
          }
        });
      })(requiredInputs[i]);
    }
  }

  /* ── Service type: update error visibility on change ─────────── */
  function attachServiceTypeListeners(form) {
    var checkboxes = form.querySelectorAll('[data-service-type]');
    for (var i = 0; i < checkboxes.length; i++) {
      checkboxes[i].addEventListener('change', function () {
        var anyChecked = form.querySelectorAll('[data-service-type]:checked').length > 0;
        var svcErr = $(SERVICE_ERROR);
        if (svcErr && anyChecked) svcErr.hidden = true;
      });
    }
  }

  /* ── Init ─────────────────────────────────────────────────────── */
  function init() {
    var form = $(FORM_ID);
    if (!form) return;

    initMinDate();
    attachLiveValidation(form);
    attachServiceTypeListeners(form);

    form.addEventListener('submit', onSubmit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
